/**
 * Studio 의 크기 제약(값)만 소유하는 모듈. 배치(grid/flex/scroll)와 색은 `*Layout` 이 갖는다.
 *
 * 값과 표현을 나누는 목적은 **합이 성립하는지 물을 수 있게 하는 것**이다. 제약이 styled 템플릿
 * 안에 흩어져 있으면 "루트가 선언한 바닥이 자기 내부 바닥의 합보다 큰가"를 물을 대상이 없고,
 * 그 합이 어긋나면 선언한 최소보다 큰 높이에서도 내용이 잘린다. 여기 모인 값은 수치라서 더할 수
 * 있고, `MediaAnnotationStudioConstraint.test.ts` 가 그 합을 계약으로 잡는다.
 *
 * 규칙:
 * - 길이는 rem **수치**로 두고 `rem()` 으로 문자열을 만든다. 수치여야 합·비교가 가능하다.
 * - `*_MIN_*` 은 바닥(더 줄지 않는 선), `*_IDEAL_*` 은 여유가 있을 때의 기준(flex basis)이다.
 *   이상 높이를 바닥으로 쓰면 칼럼이 그 높이에서 굳어 세로가 모자라도 줄지 못한다.
 * - 컨테이너 쿼리 임계도 여기서 소유한다. 폭 분기와 폭 clamp 는 같은 예산의 두 얼굴이다.
 * - 섹션 **안쪽**의 지역 최소(목록 viewport, placeholder 등)는 각 컴포넌트가 갖는다. 이 모듈은
 *   칼럼과 루트의 바닥을 정하는 항목만 모은다.
 */

/**
 * 수치를 CSS 길이로 만든다. 파생값은 소수 rem 을 더해 만들어지므로 부동소수 잡음(0.1 + 0.2)이
 * 그대로 CSS 로 나가지 않게 자른다 — 자르지 않으면 `24.799999999999997rem` 같은 선언이 남는다.
 */
export const rem = (value: number): string => `${Number(value.toFixed(4))}rem`;

// ── 패널 공통 chrome ─────────────────────────────────────────────────────────
// 두 축이 함께 읽는 값이라 축보다 먼저 선언한다.

export const ANNOTATION_PANEL_PADDING_REM = 0.65;
export const ANNOTATION_PANEL_GAP_REM = 0.4;
export const ANNOTATION_PANEL_BODY_GAP_REM = 0.35;
export const ANNOTATION_PANEL_HEADER_MIN_HEIGHT_REM = 1.75;
export const PANEL_SECTION_PADDING_TOP_REM = 0.4;
export const PANEL_SECTION_GAP_REM = 0.3;

// ── 폭 축 ────────────────────────────────────────────────────────────────────
// 레일과 패널은 Studio 컨테이너 폭에 비례해 자라고 양끝에서 멈춘다. 가운데 미디어 칼럼이
// 나머지를 받는다.

export const STUDIO_RAIL_WIDTH = { min: rem(14), ideal: "20cqw", max: rem(19) } as const;

/**
 * 도구 푸터(초기화 · 저장)가 한 줄을 유지하는 데 필요한 폭. 두 버튼의 chrome(패딩 + 테두리 +
 * 아이콘 + 아이콘-라벨 갭)과 각 라벨, 그리고 두 버튼 사이 갭이다.
 *
 * 라벨 폭은 c6 가 계산할 수 없으므로(로케일마다 다르다) **선언된 예산**으로 둔다 — 지금 싣고 있는
 * 라벨이 이 예산에 들어가는지는 `AnnotationToolsPanel.browser.test.tsx` 가 실제 Pretendard 로
 * ko·en 양쪽을 렌더해 확인한다. 라벨을 바꾸면 그 테스트가 먼저 말한다.
 */
export const TOOLS_FOOTER_BUTTON_CHROME_REM = 2.8125;
export const TOOLS_FOOTER_DISCARD_LABEL_REM = 5.875;
export const TOOLS_FOOTER_SAVE_LABEL_REM = 6.9375;
export const TOOLS_FOOTER_GAP_REM = 0.35;

export const ANNOTATION_TOOLS_FOOTER_MIN_WIDTH_REM =
  TOOLS_FOOTER_BUTTON_CHROME_REM * 2 +
  TOOLS_FOOTER_DISCARD_LABEL_REM +
  TOOLS_FOOTER_SAVE_LABEL_REM +
  TOOLS_FOOTER_GAP_REM;

/** 0.25rem 격자로 올림한다 — 파생 폭이 선언으로 나갈 때 읽기 쉬운 단에 서게 한다. */
const ceilToQuarterRem = (value: number): number => Math.ceil(value * 4) / 4;

/**
 * annotation panel 칼럼의 바닥 폭. 패널 안에서 가로로 가장 많이 요구하는 것이 도구 푸터이고,
 * 그 아래로 좁아지면 액션 라벨이 잘린다. 임의의 값을 박지 않는다.
 */
export const ANNOTATION_PANEL_MIN_WIDTH_REM = ceilToQuarterRem(
  ANNOTATION_TOOLS_FOOTER_MIN_WIDTH_REM + ANNOTATION_PANEL_PADDING_REM * 2,
);

export const STUDIO_PANEL_WIDTH = {
  min: rem(ANNOTATION_PANEL_MIN_WIDTH_REM),
  ideal: "26cqw",
  max: rem(24),
} as const;

/**
 * 좁은 데스크톱에서는 비례 확장을 멈추고 고정 폭으로 내려간다. 그 고정 폭은 바닥과 같다 —
 * 패널은 자기 내용이 요구하는 폭보다 좁아질 수 없다.
 */
export const STUDIO_PANEL_WIDTH_COMPACT = rem(ANNOTATION_PANEL_MIN_WIDTH_REM);

/** 컨테이너 쿼리 임계. 폭 clamp 와 함께 읽어야 의미가 통한다. */
export const STUDIO_BREAKPOINT = {
  /** 이 아래에서 패널이 비례 확장을 멈추고 고정 폭이 된다. */
  compactPanel: rem(67.5),
  /** 이 아래에서 레일이 위로 접히고 워크스페이스가 세로 스크롤을 갖는다. */
  stackedRail: rem(51.25),
  /** 이 아래에서 패널이 미디어 아래로 쌓인다. */
  stackedPanel: rem(42.5),
  /** 이 아래에서 커맨드 바와 레일 헤더가 최소 높이를 다시 요구한다. */
  tightCommandBar: rem(34),
} as const;

// ── 높이 축 ──────────────────────────────────────────────────────────────────
// 루트 = 커맨드 바 + 워크스페이스. 워크스페이스에서 가장 키가 큰 칼럼이 annotation panel 이라
// 루트의 바닥은 사실상 그 칼럼의 바닥이다.
//
// 세로가 모자라면 캡처 프레임 목록의 여유 높이부터 양보한다.
// 프레임 목록은 한 행까지 먼저 줄이고 슬롯 도구 공간을 확보한다. 그보다 짧으면 Inspector의
// 공용 스크롤로 남은 슬롯을 탐색한다. 편집 대상 영상과 페이지 이동 버튼은 유지한다.

export const STUDIO_COMMAND_BAR_HEIGHT_REM = 3;

/** 도구와 placeholder 모두 측정할 수 없는 상태의 Inspector 우선 높이. 실측 가능한 내용이 우선한다. */
export const ANNOTATION_INSPECTOR_PREFERRED_HEIGHT_REM = 26;

/**
 * 인스펙터 — 세로가 모자라면 여기까지 양보한다. 줄어든 만큼은 `AnnotationInspectorBody` 의 세로
 * 스크롤이 받으므로 내용에 닿지 못하는 구간이 생기지 않는다.
 */
export const ANNOTATION_INSPECTOR_MIN_HEIGHT_REM = 8;

/**
 * 캡처 프레임 목록 viewport의 바닥은 한 행이다. 페이지가 짧으면 남는 높이는 Inspector에 돌려준다.
 */
export const CAPTURED_FRAME_LIST_ROW_HEIGHT_REM = 2.8125;

/**
 * 범위 필터의 최대 chrome — `sm` 스위처와 활성 필터의 요약 한 줄을 덮는다.
 * 전체 episode에서는 요약을 접고 그 공간을 목록에 돌려준다. 외부 최소 높이는 필터 활성 상태도 수용한다.
 */
export const CAPTURED_FRAME_FILTER_PADDING_REM = 0;
export const CAPTURED_FRAME_FILTER_GAP_REM = 0.2;
export const CAPTURED_FRAME_SCOPE_SWITCHER_HEIGHT_REM = 1.75;
export const CAPTURED_FRAME_FILTER_SUMMARY_HEIGHT_REM = 1.05;
export const CAPTURED_FRAME_FILTER_SUMMARY_EXTRA_HEIGHT = rem(
  CAPTURED_FRAME_FILTER_GAP_REM + CAPTURED_FRAME_FILTER_SUMMARY_HEIGHT_REM,
);

export const CAPTURED_FRAME_FILTER_MIN_HEIGHT_REM =
  CAPTURED_FRAME_FILTER_PADDING_REM * 2 +
  CAPTURED_FRAME_SCOPE_SWITCHER_HEIGHT_REM +
  CAPTURED_FRAME_FILTER_GAP_REM +
  CAPTURED_FRAME_FILTER_SUMMARY_HEIGHT_REM;

/** 페이지네이션 — 다른 페이지로 가는 유일한 길이라 섹션 바닥이 반드시 덮어야 한다. */
export const CAPTURED_FRAME_PAGINATION_MIN_HEIGHT_REM = 1.75;

/**
 * 목록 본문의 바닥 — 섹션 헤더 + 범위 필터 + 목록 한 행 + 페이지네이션과 그 사이 갭.
 * 섹션 헤더는 이 본문 **안**에 있다. 밖에 있다고 세면 헤더 한 줄만큼 바닥이 모자라고, 그 부족분은
 * 목록이 아니라 맨 아래 페이지네이션에서 드러난다.
 */
export const ANNOTATION_CAPTURED_FRAME_CONTENT_MIN_HEIGHT_REM =
  ANNOTATION_PANEL_HEADER_MIN_HEIGHT_REM +
  PANEL_SECTION_GAP_REM +
  CAPTURED_FRAME_FILTER_MIN_HEIGHT_REM +
  PANEL_SECTION_GAP_REM +
  CAPTURED_FRAME_LIST_ROW_HEIGHT_REM +
  PANEL_SECTION_GAP_REM +
  CAPTURED_FRAME_PAGINATION_MIN_HEIGHT_REM;

/**
 * 캡처 프레임 섹션의 바닥 — 섹션 안의 부품을 모두 덮는다. 목록 하나만 세면 페이지네이션이
 * 섹션 밖으로 밀려난다. 닫힌 상태에는 기준(basis)이 없다 — 비율이 본문 세로를 나눈다.
 */
export const ANNOTATION_CAPTURED_FRAME_SECTION_MIN_HEIGHT_REM =
  PANEL_SECTION_PADDING_TOP_REM + ANNOTATION_CAPTURED_FRAME_CONTENT_MIN_HEIGHT_REM;

/** 두 배치 모두 이미지 읽기 영역과 고정 도구를 확보한다. inline일 때만 패널 예산에 포함한다. */
export const ANNOTATION_CAPTURED_FRAME_PREVIEW_IMAGE_MIN_HEIGHT_REM = 10;
/** 카드 padding/border + 28px 헤더 + 26px 슬롯 줄 + 두 gap. */
export const ANNOTATION_CAPTURED_FRAME_PREVIEW_CHROME_MIN_HEIGHT_REM =
  0.4 * 2 + 0.0625 * 2 + 1.75 + 1.625 + 0.35 * 2;
export const ANNOTATION_CAPTURED_FRAME_PREVIEW_MIN_HEIGHT_REM =
  ANNOTATION_CAPTURED_FRAME_PREVIEW_IMAGE_MIN_HEIGHT_REM +
  ANNOTATION_CAPTURED_FRAME_PREVIEW_CHROME_MIN_HEIGHT_REM;
/**
 * 미리보기 팝업의 크기. 폭은 패널 최대 폭과 같아 미디어 칼럼 위에서 패널 하나만큼만 차지하고,
 * 높이는 chrome 을 뺀 이미지 영역이 4:3 프레임을 폭에 맞춰 채우는 값이다.
 */
export const ANNOTATION_CAPTURED_FRAME_PREVIEW_POPUP_WIDTH_REM = 24;
export const ANNOTATION_CAPTURED_FRAME_PREVIEW_POPUP_HEIGHT_REM = 19;

/**
 * annotation panel 칼럼의 바닥. 패딩 두 번 + 헤더 + 헤더-본문 갭 + 두 섹션 바닥 + 섹션 사이 갭.
 * 미리보기는 여유 공간에 넣거나 팝업으로 분리하므로 이 합에 들어가지 않는다.
 */
export const ANNOTATION_PANEL_MIN_HEIGHT_REM =
  ANNOTATION_PANEL_PADDING_REM * 2 +
  ANNOTATION_PANEL_HEADER_MIN_HEIGHT_REM +
  ANNOTATION_PANEL_GAP_REM +
  ANNOTATION_INSPECTOR_MIN_HEIGHT_REM +
  ANNOTATION_PANEL_BODY_GAP_REM +
  ANNOTATION_CAPTURED_FRAME_SECTION_MIN_HEIGHT_REM;

/**
 * 제품이 심는 선두 자리. c6 는 그 안에 무엇이 오는지 모르므로 자리 자체가 상한을 갖고, 넘치면
 * 자리가 스크롤한다. 상한은 자리를 담는 `AnnotationPanelBody` 기준이다.
 *
 * 자리는 아래 두 섹션보다 **먼저 양보한다** — 자기 스크롤러를 이미 갖고 있어 줄어도 내용에 닿을
 * 수 있다는, 양보 순서의 같은 기준을 따른다. 바닥은 자리가 아예 사라지지 않을 만큼만 둔다.
 */
export const ANNOTATION_PANEL_LEAD_SECTION_MIN_HEIGHT_REM = 2.5;
export const ANNOTATION_PANEL_LEAD_SECTION_MAX_SHARE = "40%";
export const ANNOTATION_PANEL_LEAD_SECTION_GAP_REM = 0.4;

/**
 * 선두 자리를 세울 때 패널 칼럼이 추가로 요구하는 세로 — 자리의 바깥 바닥 + 다음 섹션과의 갭.
 * 자리는 `border-box` 라 padding·border 는 이미 그 바닥 안에 있다.
 * 자리를 주지 않은 소비처의 바닥은 그대로여야 하므로 기본 바닥에 합치지 않고 따로 둔다.
 */
export const ANNOTATION_PANEL_LEAD_SECTION_RESERVE_REM =
  ANNOTATION_PANEL_LEAD_SECTION_MIN_HEIGHT_REM + ANNOTATION_PANEL_BODY_GAP_REM;

export const ANNOTATION_PANEL_MIN_HEIGHT_WITH_LEAD_REM =
  ANNOTATION_PANEL_MIN_HEIGHT_REM + ANNOTATION_PANEL_LEAD_SECTION_RESERVE_REM;

/** 미디어 칼럼(뷰포트 + 재생 컨트롤)의 바닥. */
export const STUDIO_MEDIA_COLUMN_MIN_HEIGHT_REM = 20;

/** Studio 루트의 바닥 — 커맨드 바 + 가장 키가 큰 칼럼. 임의의 값을 박지 않는다. */
export const STUDIO_MIN_HEIGHT_REM =
  STUDIO_COMMAND_BAR_HEIGHT_REM +
  Math.max(ANNOTATION_PANEL_MIN_HEIGHT_REM, STUDIO_MEDIA_COLUMN_MIN_HEIGHT_REM);

export const STUDIO_MIN_HEIGHT_WITH_LEAD_REM =
  STUDIO_COMMAND_BAR_HEIGHT_REM +
  Math.max(ANNOTATION_PANEL_MIN_HEIGHT_WITH_LEAD_REM, STUDIO_MEDIA_COLUMN_MIN_HEIGHT_REM);

/** 패널이 미디어 아래로 쌓이는 폭에서는 두 행이 세로로 더해진다. */
export const STUDIO_STACKED_MIN_HEIGHT_REM =
  STUDIO_COMMAND_BAR_HEIGHT_REM +
  STUDIO_MEDIA_COLUMN_MIN_HEIGHT_REM +
  ANNOTATION_PANEL_MIN_HEIGHT_REM;

export const STUDIO_STACKED_MIN_HEIGHT_WITH_LEAD_REM =
  STUDIO_STACKED_MIN_HEIGHT_REM + ANNOTATION_PANEL_LEAD_SECTION_RESERVE_REM;

// ── styled 템플릿이 그대로 쓰는 문자열 ──────────────────────────────────────
export const STUDIO_COMMAND_BAR_HEIGHT = rem(STUDIO_COMMAND_BAR_HEIGHT_REM);
export const STUDIO_MIN_HEIGHT = rem(STUDIO_MIN_HEIGHT_REM);
export const STUDIO_MIN_HEIGHT_WITH_LEAD = rem(STUDIO_MIN_HEIGHT_WITH_LEAD_REM);
export const STUDIO_STACKED_MIN_HEIGHT = rem(STUDIO_STACKED_MIN_HEIGHT_REM);
export const STUDIO_STACKED_MIN_HEIGHT_WITH_LEAD = rem(STUDIO_STACKED_MIN_HEIGHT_WITH_LEAD_REM);
export const STUDIO_MEDIA_COLUMN_MIN_HEIGHT = rem(STUDIO_MEDIA_COLUMN_MIN_HEIGHT_REM);
export const ANNOTATION_PANEL_MIN_HEIGHT = rem(ANNOTATION_PANEL_MIN_HEIGHT_REM);
export const ANNOTATION_PANEL_MIN_HEIGHT_WITH_LEAD = rem(ANNOTATION_PANEL_MIN_HEIGHT_WITH_LEAD_REM);
export const ANNOTATION_PANEL_LEAD_SECTION_MIN_HEIGHT = rem(
  ANNOTATION_PANEL_LEAD_SECTION_MIN_HEIGHT_REM,
);
export const ANNOTATION_PANEL_LEAD_SECTION_GAP = rem(ANNOTATION_PANEL_LEAD_SECTION_GAP_REM);
export const ANNOTATION_PANEL_PADDING = rem(ANNOTATION_PANEL_PADDING_REM);
export const ANNOTATION_PANEL_GAP = rem(ANNOTATION_PANEL_GAP_REM);
export const ANNOTATION_PANEL_BODY_GAP = rem(ANNOTATION_PANEL_BODY_GAP_REM);
export const ANNOTATION_PANEL_HEADER_MIN_HEIGHT = rem(ANNOTATION_PANEL_HEADER_MIN_HEIGHT_REM);
export const ANNOTATION_INSPECTOR_MIN_HEIGHT = rem(ANNOTATION_INSPECTOR_MIN_HEIGHT_REM);
export const ANNOTATION_CAPTURED_FRAME_SECTION_MIN_HEIGHT = rem(
  ANNOTATION_CAPTURED_FRAME_SECTION_MIN_HEIGHT_REM,
);
export const ANNOTATION_CAPTURED_FRAME_CONTENT_MIN_HEIGHT = rem(
  ANNOTATION_CAPTURED_FRAME_CONTENT_MIN_HEIGHT_REM,
);
export const CAPTURED_FRAME_LIST_ROW_HEIGHT = rem(CAPTURED_FRAME_LIST_ROW_HEIGHT_REM);
export const CAPTURED_FRAME_PAGINATION_MIN_HEIGHT = rem(CAPTURED_FRAME_PAGINATION_MIN_HEIGHT_REM);
export const PANEL_SECTION_PADDING_TOP = rem(PANEL_SECTION_PADDING_TOP_REM);
export const PANEL_SECTION_GAP = rem(PANEL_SECTION_GAP_REM);

export const ANNOTATION_CAPTURED_FRAME_PREVIEW_IMAGE_MIN_HEIGHT = rem(
  ANNOTATION_CAPTURED_FRAME_PREVIEW_IMAGE_MIN_HEIGHT_REM,
);
export const ANNOTATION_CAPTURED_FRAME_PREVIEW_MIN_HEIGHT = rem(
  ANNOTATION_CAPTURED_FRAME_PREVIEW_MIN_HEIGHT_REM,
);
export const ANNOTATION_CAPTURED_FRAME_PREVIEW_POPUP_WIDTH = rem(
  ANNOTATION_CAPTURED_FRAME_PREVIEW_POPUP_WIDTH_REM,
);
export const ANNOTATION_CAPTURED_FRAME_PREVIEW_POPUP_HEIGHT = rem(
  ANNOTATION_CAPTURED_FRAME_PREVIEW_POPUP_HEIGHT_REM,
);
